import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View, Text, StyleSheet, TextInput, TouchableOpacity,
  ActivityIndicator, ScrollView, useWindowDimensions,
} from 'react-native';
import {
  getRestaurantPayroll, getMonthTransactions, recordPayout,
  RestaurantPayrollRow, Txn, OrderLite,
} from '../query/payrollService';
import {
  C, MONTHS, inr, txnDate, MIcon, RightDrawer, PayModal, FilterChips,
  StatusBadge, rowStatus, KV, sx,
} from './PayrollShared';

const PAY_METHODS = ['Bank Transfer', 'UPI', 'Cash', 'Other'];

export default function RestaurantPayroll() {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());
  const [tab, setTab] = useState<'payouts' | 'ledger'>('payouts');
  const [search, setSearch] = useState('');

  const [rows, setRows] = useState<RestaurantPayrollRow[]>([]);
  const [txns, setTxns] = useState<Txn[]>([]);
  const [loading, setLoading] = useState(true);

  const [detail, setDetail] = useState<RestaurantPayrollRow | null>(null);
  const [orderFilter, setOrderFilter] = useState(0); // 0 all, 1 offers, 2 free delivery
  const [pay, setPay] = useState<{ row: RestaurantPayrollRow; full: boolean } | null>(null);

  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2600);
  };

  const isCurrentMonth = month === now.getMonth() && year === now.getFullYear();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, t] = await Promise.all([getRestaurantPayroll(month, year), getMonthTransactions(month, year)]);
      setRows(r);
      setTxns(t);
      setDetail(prev => (prev ? r.find(x => x.entityId === prev.entityId) ?? null : null));
    } catch (e) {
      console.error('restaurant payroll load failed', e);
      showToast('Failed to load payouts', 'error');
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const changeMonth = (delta: number) => {
    let m = month + delta, y = year;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    if (y > now.getFullYear() || (y === now.getFullYear() && m > now.getMonth())) return;
    setMonth(m); setYear(y);
  };

  const summary = useMemo(() => {
    let outstanding = 0, paid = 0, paidCount = 0, dueCount = 0;
    rows.forEach(r => {
      outstanding += r.remaining;
      paid += r.paidSoFar;
      if (r.isPaid) paidCount++;
      else if (r.net > 0) dueCount++;
    });
    return { outstanding, paid, paidCount, dueCount };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => r.name.toLowerCase().includes(q) || r.entityId.toLowerCase().includes(q));
  }, [rows, search]);

  const restTxns = useMemo(() => txns.filter(t => (t.type ?? 'restaurant') === 'restaurant'), [txns]);
  const filteredTxns = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return restTxns;
    return restTxns.filter(t => (t.entityName || '').toLowerCase().includes(q) || (t.referenceId || '').toLowerCase().includes(q));
  }, [restTxns, search]);

  const submitPay = async ({ amount, method, reference, notes }: { amount: number; method: string; reference: string; notes: string }) => {
    if (!pay) return;
    const r = pay.row;
    await recordPayout({
      type: 'restaurant', entityId: r.entityId, entityName: r.name, amount,
      gross: r.gross, deductions: r.totalDeduction, orderCount: r.orderCount,
      periodMonth: month, periodYear: year, referenceId: reference, paymentMethod: method, notes: notes || undefined,
    });
    setPay(null);
    showToast('Payout recorded');
    await load();
  };

  const Th = ({ label, flex, align }: { label: string; flex: number; align?: 'center' | 'right' }) => (
    <View style={[tb.th, { flex }, align === 'center' && { alignItems: 'center' }, align === 'right' && { alignItems: 'flex-end' }]}>
      <Text style={tb.thTxt}>{label}</Text>
    </View>
  );

  const renderPayouts = () => (
    <View style={tb.table}>
      <View style={tb.headerRow}>
        <Th label="Restaurant" flex={2.2} />
        <Th label="Orders" flex={0.8} align="center" />
        <Th label="Gross" flex={1.1} align="right" />
        <Th label="Comm" flex={1.1} align="right" />
        <Th label="Offers/FreeDel" flex={1.3} align="right" />
        <Th label="Net" flex={1.1} align="right" />
        <Th label="Status" flex={1} align="center" />
        <Th label="" flex={1.1} align="right" />
      </View>
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 30 }}>
        {filteredRows.length === 0 ? (
          <View style={sx.empty}><Text style={sx.emptyTxt}>No restaurant payroll this month</Text></View>
        ) : filteredRows.map((r, i) => (
          <TouchableOpacity key={r.entityId} style={[tb.row, i % 2 === 1 && tb.rowAlt]} onPress={() => { setDetail(r); setOrderFilter(0); }}>
            <View style={[tb.td, { flex: 2.2 }]}>
              <Text style={tb.name} numberOfLines={1}>{r.name}</Text>
              <Text style={tb.sub} numberOfLines={1}>{r.entityId}</Text>
            </View>
            <Text style={[tb.cell, { flex: 0.8, textAlign: 'center' }]}>{r.orderCount}</Text>
            <Text style={[tb.cell, { flex: 1.1, textAlign: 'right' }]}>{inr(r.gross)}</Text>
            <Text style={[tb.cell, { flex: 1.1, textAlign: 'right', color: C.danger }]}>{inr(r.commission)}</Text>
            <Text style={[tb.cell, { flex: 1.3, textAlign: 'right', color: C.danger }]}>{r.promoDeduction ? `-${inr(r.promoDeduction)}` : '—'}</Text>
            <Text style={[tb.cell, { flex: 1.1, textAlign: 'right', fontWeight: '800' }]}>{inr(r.net)}</Text>
            <View style={[tb.td, { flex: 1, alignItems: 'center' }]}><StatusBadge status={rowStatus(r.isPaid, r.paidSoFar)} /></View>
            <View style={[tb.td, { flex: 1.1, alignItems: 'flex-end' }]}>
              {!r.isPaid && r.net > 0 ? (
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Text style={sx.tapPayTxt}>Pay</Text>
                  <MIcon name="chevron_right" size={18} color={C.accent} />
                </View>
              ) : <Text style={tb.muted}>—</Text>}
            </View>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderLedger = () => (
    <View style={tb.table}>
      <View style={tb.headerRow}>
        <Th label="Restaurant" flex={2.2} />
        <Th label="Amount" flex={1.1} align="right" />
        <Th label="Method" flex={1.2} />
        <Th label="Reference" flex={1.4} />
        <Th label="Date" flex={1.5} align="right" />
      </View>
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 30 }}>
        {filteredTxns.length === 0 ? (
          <View style={sx.empty}><Text style={sx.emptyTxt}>No payouts this month</Text></View>
        ) : filteredTxns.map((t, i) => (
          <View key={t.id} style={[tb.row, i % 2 === 1 && tb.rowAlt]}>
            <View style={[tb.td, { flex: 2.2 }]}>
              <Text style={tb.name} numberOfLines={1}>{t.entityName || t.entityId}</Text>
              <Text style={tb.sub} numberOfLines={1}>{t.entityId}</Text>
            </View>
            <Text style={[tb.cell, { flex: 1.1, textAlign: 'right', color: C.accent, fontWeight: '800' }]}>{inr(t.amount)}</Text>
            <Text style={[tb.cell, { flex: 1.2 }]} numberOfLines={1}>{t.paymentMethod || '—'}</Text>
            <Text style={[tb.cell, { flex: 1.4 }]} numberOfLines={1}>{t.referenceId || '—'}</Text>
            <Text style={[tb.cell, { flex: 1.5, textAlign: 'right' }]} numberOfLines={1}>{txnDate(t.paidAt)}</Text>
          </View>
        ))}
      </ScrollView>
    </View>
  );

  const drawerOrders = useMemo(() => {
    if (!detail) return [] as OrderLite[];
    const list = [...detail.orders].sort((a, b) => (b.deliveredAt?.getTime() ?? b.createdAt?.getTime() ?? 0) - (a.deliveredAt?.getTime() ?? a.createdAt?.getTime() ?? 0));
    if (orderFilter === 1) return list.filter(o => o.offerDiscount > 0);
    if (orderFilter === 2) return list.filter(o => o.freeDelivery);
    return list;
  }, [detail, orderFilter]);

  const offersCount = detail ? detail.orders.filter(o => o.offerDiscount > 0).length : 0;
  const freeCount = detail ? detail.orders.filter(o => o.freeDelivery).length : 0;

  return (
    <View style={sx.screen}>
      <View style={sx.monthRow}>
        <TouchableOpacity style={sx.monthBtn} onPress={() => changeMonth(-1)}><Text style={sx.monthBtnTxt}>‹</Text></TouchableOpacity>
        <View style={sx.monthLabelWrap}><Text style={sx.monthLabel}>{MONTHS[month]} {year}</Text></View>
        <TouchableOpacity style={[sx.monthBtn, isCurrentMonth && sx.monthBtnDisabled]} onPress={() => changeMonth(1)} disabled={isCurrentMonth}>
          <Text style={[sx.monthBtnTxt, isCurrentMonth && sx.monthBtnTxtDisabled]}>›</Text>
        </TouchableOpacity>
      </View>

      <View style={sx.summaryCard}>
        <View style={[sx.summaryRow, isMobile && { flexWrap: 'wrap', gap: 16 }]}>
          <View style={sx.summaryItem}>
            <Text style={sx.summaryLabel}>Total Outstanding</Text>
            <Text style={sx.summaryValueBig}>{inr(summary.outstanding)}</Text>
            <Text style={sx.summaryCaption}>{summary.dueCount} due</Text>
          </View>
          <View style={sx.summaryDivider} />
          <View style={sx.summaryItem}>
            <Text style={sx.summaryLabel}>Paid This Month</Text>
            <Text style={sx.summaryValue}>{inr(summary.paid)}</Text>
            <Text style={sx.summaryCaption}>{summary.paidCount} fully paid</Text>
          </View>
          <View style={sx.summaryDivider} />
          <View style={sx.summaryItem}>
            <Text style={sx.summaryLabel}>Restaurants</Text>
            <Text style={sx.summaryValue}>{rows.length}</Text>
            <Text style={sx.summaryCaption}>{summary.paidCount} paid · {summary.dueCount} due</Text>
          </View>
        </View>
      </View>

      <View style={[sx.controls, isMobile && { flexDirection: 'column', alignItems: 'stretch', gap: 10 }]}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginRight: 'auto' as any }}>
          <MIcon name="payments" size={20} color="#4ECDC4" />
          <View>
            <Text style={{ fontSize: 15, fontWeight: '800', color: '#fff' }}>Restaurant Payouts</Text>
            <Text style={{ fontSize: 11, color: '#8BAAB8', marginTop: 1 }}>Settlement reports for restaurants</Text>
          </View>
        </View>
        <View style={sx.tabRow}>
          {([['payouts', 'Payouts'], ['ledger', 'Ledger']] as ['payouts' | 'ledger', string][]).map(([t, lbl]) => (
            <TouchableOpacity key={t} style={[sx.tab, tab === t && sx.tabActive]} onPress={() => setTab(t)}>
              <Text style={[sx.tabTxt, tab === t && sx.tabTxtActive]}>{lbl}</Text>
            </TouchableOpacity>
          ))}
        </View>
        <View style={[sx.searchBox, { flex: 1 }]}>
          <MIcon name="search" size={15} color="#8BAAB8" style={{ marginRight: 6 }} />
          <TextInput style={sx.searchInput} placeholder="Search by name or id…" value={search} onChangeText={setSearch} placeholderTextColor="#8BAAB8" />
        </View>
        <TouchableOpacity style={sx.refreshBtn} onPress={load}><MIcon name="refresh" size={18} color="#4ECDC4" /></TouchableOpacity>
      </View>

      {loading ? (
        <View style={sx.loading}><ActivityIndicator size="large" color={C.teal} /><Text style={sx.loadingTxt}>Loading payouts…</Text></View>
      ) : (
        <ScrollView horizontal showsHorizontalScrollIndicator style={{ flex: 1 }} contentContainerStyle={{ minWidth: 1000, flexGrow: 1 }}>
          {tab === 'payouts' ? renderPayouts() : renderLedger()}
        </ScrollView>
      )}

      <RightDrawer visible={!!detail} onClose={() => setDetail(null)} title={detail?.name || ''} subtitle={`${MONTHS[month]} ${year} · Restaurant payout`}>
        {detail && (
          <>
            <View style={sx.drawerCard}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text style={{ fontSize: 13, fontWeight: '800', color: C.text }}>Net Payout</Text>
                <Text style={{ fontSize: 22, fontWeight: '900', color: C.accent }}>{inr(detail.net)}</Text>
              </View>
              <View style={{ height: 1, backgroundColor: C.border, marginVertical: 8 }} />
              <KV label="Gross Earnings (Sales)" value={inr(detail.gross)} />
              <Text style={[sx.sectionLabel, { marginTop: 8, marginBottom: 4 }]}>DEDUCTIONS</Text>
              <KV label={`Platform Commission (${detail.commissionPct}%)`} value={`-${inr(detail.commission)}`} color={C.danger} />
              {detail.freeDeliveryDeduction > 0 && <KV label="Free Delivery (restaurant-funded)" value={`-${inr(detail.freeDeliveryDeduction)}`} color={C.danger} />}
              {detail.offerDeduction > 0 && <KV label="Offers & Discounts (restaurant-funded)" value={`-${inr(detail.offerDeduction)}`} color={C.danger} />}
              <View style={{ height: 1, backgroundColor: C.border, marginVertical: 8 }} />
              <KV label="Total Deductions" value={`-${inr(detail.totalDeduction)}`} color={C.danger} />
              <KV label="Paid So Far" value={inr(detail.paidSoFar)} color={C.accent} />
              <KV label="Remaining" value={inr(detail.remaining)} color={detail.remaining > 0 ? C.danger : C.muted} bold />
            </View>

            {!detail.isPaid && detail.net > 0 && (
              <View style={sx.payBtnRow}>
                <TouchableOpacity style={sx.partialBtn} onPress={() => setPay({ row: detail, full: false })}><Text style={sx.partialTxt}>Pay Partial</Text></TouchableOpacity>
                <TouchableOpacity style={sx.fullBtn} onPress={() => setPay({ row: detail, full: true })}><Text style={sx.fullTxt}>Pay Full {inr(detail.remaining)}</Text></TouchableOpacity>
              </View>
            )}

            <Text style={sx.sectionLabel}>ORDER BREAKDOWN · {detail.orderCount} ORDERS</Text>
            <FilterChips
              value={orderFilter}
              onChange={setOrderFilter}
              options={[
                { label: 'All', value: 0, count: detail.orders.length },
                { label: 'Offers', value: 1, count: offersCount },
                { label: 'Free Delivery', value: 2, count: freeCount },
              ]}
            />
            {drawerOrders.length === 0 ? (
              <View style={[sx.orderCard, { alignItems: 'center', paddingVertical: 22 }]}><Text style={{ color: C.muted, fontSize: 12.5, fontWeight: '600' }}>No orders</Text></View>
            ) : drawerOrders.map(o => <OrderRow key={o.id} o={o} commissionPct={detail.commissionPct} />)}
          </>
        )}
      </RightDrawer>

      <PayModal
        visible={!!pay}
        onClose={() => setPay(null)}
        title={pay?.full ? 'Pay Full' : 'Partial Payment'}
        entityName={pay?.row.name || ''}
        remaining={pay?.row.remaining || 0}
        full={!!pay?.full}
        methods={PAY_METHODS}
        onConfirm={submitPay}
      />

      {toast && (
        <View style={[styles.toast, { backgroundColor: toast.type === 'error' ? C.danger : C.accent }]}>
          <Text style={styles.toastTxt}>{toast.type === 'error' ? '✕ ' : '✓ '}{toast.msg}</Text>
        </View>
      )}
    </View>
  );
}

function OrderRow({ o, commissionPct }: { o: OrderLite; commissionPct: number }) {
  const t = o.deliveredAt ?? o.createdAt;
  const time = t ? t.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
  const commission = (o.productAmount * commissionPct) / 100;
  const restFreeDel = o.restaurantPromo > 0 && o.freeDelivery ? o.freeDeliveryAmount : 0;
  const restOffer = o.restaurantPromo > 0 ? o.offerDiscount : 0;
  const net = Math.max(0, o.productAmount - commission - restFreeDel - restOffer);
  return (
    <View style={sx.orderCard}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <Text style={{ fontSize: 13, fontWeight: '800', color: C.text }}>#{o.id.slice(0, 6).toUpperCase()}</Text>
        <Text style={{ fontSize: 13.5, fontWeight: '900', color: C.accent }}>{inr(net)}</Text>
      </View>
      {time ? <Text style={{ fontSize: 10.5, color: C.muted, marginTop: 2 }}>{time}</Text> : null}
      <View style={{ height: 1, backgroundColor: C.border, marginVertical: 8 }} />
      <Mini label="Items Subtotal" value={inr(o.productAmount)} color={C.muted} />
      <Mini label={`Platform Commission (${commissionPct}%)`} value={`-${inr(commission)}`} color={C.danger} />
      {restFreeDel > 0 && <Mini label="Free Delivery absorbed" value={`-${inr(restFreeDel)}`} color={C.danger} />}
      {restOffer > 0 && <Mini label="Offer Discount absorbed" value={`-${inr(restOffer)}`} color={C.danger} />}
    </View>
  );
}

const Mini = ({ label, value, color }: { label: string; value: string; color: string }) => (
  <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 1.5 }}>
    <Text style={{ fontSize: 11, color: C.muted, fontWeight: '600' }}>{label}</Text>
    <Text style={{ fontSize: 11, fontWeight: '800', color }}>{value}</Text>
  </View>
);

const styles = StyleSheet.create({
  toast: { position: 'absolute', bottom: 24, alignSelf: 'center', paddingHorizontal: 20, paddingVertical: 12, borderRadius: 10 },
  toastTxt: { color: '#fff', fontWeight: '600', fontSize: 13 },
});

const tb = StyleSheet.create({
  table: { flex: 1, backgroundColor: '#17201B', borderRadius: 14, overflow: 'hidden', borderWidth: 1, borderColor: C.border },
  headerRow: { flexDirection: 'row', backgroundColor: '#0A1712', borderBottomWidth: 1, borderBottomColor: C.border, paddingHorizontal: 14, paddingVertical: 12 },
  th: { justifyContent: 'center' },
  thTxt: { fontSize: 11, fontWeight: '800', color: C.muted, letterSpacing: 0.6, textTransform: 'uppercase' as const },
  row: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.09)' },
  rowAlt: { backgroundColor: '#1A2820' },
  td: { justifyContent: 'center', paddingRight: 8 },
  cell: { fontSize: 13, color: C.text, paddingRight: 8 },
  name: { fontSize: 14, fontWeight: '600', color: C.text },
  sub: { fontSize: 10, color: C.muted, marginTop: 1 },
  muted: { fontSize: 13, color: C.muted },
});
